import { useId, useRef } from 'react';
import { FileDownloadOutlined } from '@mui/icons-material';
import { Box, IconButton, Paper, Portal, Typography } from '@mui/material';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';

import { FHPdfViewer } from '../../../../../components/base';
import CloseIcon from '../../../../../components/svgs/CloseIcon';
import { FORTA_BLUE } from '../../../../../utils/colors';
import { DOCUMENT_PREVIEW_DRAG_HANDLE_CLASS, DOCUMENT_PREVIEW_RESIZE_HANDLES } from './constants';
import {
  useDocumentPreviewDialogFocus,
  useDocumentPreviewDialogLayout,
  useDocumentPreviewExclusive,
} from './hooks';
import {
  DOCUMENT_PREVIEW_BODY_SX,
  DOCUMENT_PREVIEW_BOUNDS_SX,
  DOCUMENT_PREVIEW_CONTENT_SX,
  DOCUMENT_PREVIEW_HEADER_SX,
  DOCUMENT_PREVIEW_IMAGE_SX,
  DOCUMENT_PREVIEW_PAPER_SX,
  DOCUMENT_PREVIEW_RESIZABLE_SX,
} from './styles';
import { isPdfDocumentFileType } from './utils';

import type { DocumentPreviewDialogProps } from './types';

export default function DocumentPreviewDialog({
  documentFileType = '',
  documentPreviewUrl,
  illustrationAlt,
  onClose,
  open,
  showDownload = false,
}: DocumentPreviewDialogProps) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const {
    bounds,
    handleDrag,
    handleResize,
    handleResizeStart,
    handleResizeStop,
    height,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth,
    width,
    x,
    y,
  } = useDocumentPreviewDialogLayout(open);

  useDocumentPreviewExclusive(open, onClose);
  useDocumentPreviewDialogFocus(open, nodeRef);

  if (!open) {
    return null;
  }

  const handleDownload = () => {
    if (!documentPreviewUrl) return;

    const link = document.createElement('a');
    link.href = documentPreviewUrl;
    link.download = illustrationAlt || 'document';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  };

  return (
    <Portal>
      <Box sx={DOCUMENT_PREVIEW_BOUNDS_SX}>
        <Draggable
          bounds={bounds}
          cancel="button"
          handle={`.${DOCUMENT_PREVIEW_DRAG_HANDLE_CLASS}`}
          nodeRef={nodeRef}
          onDrag={handleDrag}
          position={{ x, y }}
        >
          <Paper
            aria-labelledby={titleId}
            aria-modal={false}
            ref={nodeRef}
            role="dialog"
            sx={DOCUMENT_PREVIEW_PAPER_SX}
            tabIndex={-1}
          >
            <Box sx={DOCUMENT_PREVIEW_RESIZABLE_SX}>
              <ResizableBox
                axis="both"
                height={height}
                maxConstraints={[maxWidth, maxHeight]}
                minConstraints={[minWidth, minHeight]}
                onResize={handleResize}
                onResizeStart={handleResizeStart}
                onResizeStop={handleResizeStop}
                resizeHandles={[...DOCUMENT_PREVIEW_RESIZE_HANDLES]}
                width={width}
              >
                <Box sx={DOCUMENT_PREVIEW_BODY_SX}>
                  <Box
                    className={DOCUMENT_PREVIEW_DRAG_HANDLE_CLASS}
                    sx={DOCUMENT_PREVIEW_HEADER_SX}
                  >
                    <Typography fontWeight="bold" id={titleId} variant="h6">
                      Document preview
                    </Typography>
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                      {showDownload && documentPreviewUrl ? (
                        <IconButton
                          aria-label="Download"
                          disableRipple
                          onClick={handleDownload}
                          sx={{ p: 0 }}
                          title="Download"
                        >
                          <FileDownloadOutlined sx={{ color: FORTA_BLUE.primary }} />
                        </IconButton>
                      ) : null}
                      <IconButton
                        aria-label="Close"
                        disableRipple
                        onClick={onClose}
                        sx={{ p: 0 }}
                        title="Close"
                      >
                        <CloseIcon color={FORTA_BLUE.primary} />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={DOCUMENT_PREVIEW_CONTENT_SX}>
                    {isPdfDocumentFileType(documentFileType) ? (
                      <FHPdfViewer fileUrl={documentPreviewUrl} />
                    ) : (
                      <Box
                        alt={illustrationAlt}
                        component="img"
                        src={documentPreviewUrl}
                        sx={DOCUMENT_PREVIEW_IMAGE_SX}
                      />
                    )}
                  </Box>
                </Box>
              </ResizableBox>
            </Box>
          </Paper>
        </Draggable>
      </Box>
    </Portal>
  );
}
